import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Text } from '@/hen-ui/atomics/Text';
import { barVariants, type BarVariantProps } from './bar.variants';

export interface BarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    BarVariantProps {
  /** Text label displayed on the bar */
  label: string;
  /** Numeric value for proportional sizing (0-100) */
  value?: number;
  /** Explicit width in pixels (for vertical bars) */
  width?: number;
  /** Explicit height in pixels (for horizontal bars) */
  height?: number;
}

export const Bar = React.forwardRef<HTMLDivElement, BarProps>(
  (
    {
      className,
      variant,
      orientation = 'vertical',
      label,
      value,
      width,
      height,
      style,
      ...props
    },
    ref,
  ) => {
    const sizeStyle: React.CSSProperties = { ...style };

    if (orientation === 'horizontal' && value !== undefined) {
      sizeStyle.width = `${value}%`;
    } else if (orientation === 'horizontal' && width !== undefined) {
      sizeStyle.width = `${width}px`;
    }

    if (orientation === 'vertical' && value !== undefined) {
      sizeStyle.height = `${value}%`;
    } else if (orientation === 'vertical' && height !== undefined) {
      sizeStyle.height = `${height}px`;
    }

    return (
      <div
        ref={ref}
        className={cn(barVariants({ variant, orientation }), className)}
        style={sizeStyle}
        {...props}
      >
        <Text
          variant='l1'
          color='white'
          weight='bold'
          align={orientation === 'horizontal' ? 'right' : 'center'}
        >
          {label}
        </Text>
      </div>
    );
  },
);

Bar.displayName = 'Bar';
